/**
 * SkyModule (Atlas) — Phase B Hillaire procedural sky swap.
 *
 * Wraps the EngineX `SkyModule` (4-LUT Hillaire on WebGPU, inline ray-march
 * on WebGL2 via `pipeline: 'auto'`) and wires it to Atlas's per-world
 * `skyState.timeOfDay` clock.
 *
 * What this module owns:
 *   - Instantiates + inits the engine sky (which sets `scene.backgroundNode`,
 *     sets `scene.background = null`, and starts the PMREM-IBL bake cycle).
 *   - After the engine init, replaces `scene.backgroundNode` with a composite:
 *     atmosphere + a PHOTOGRAPHIC night sky (8K Higgsfield astrophotography
 *     equirect from /sky/, world-static, gated by `uNightFactor`).  The former
 *     procedural starfield / milky way / nebula / volcanic-planet layers were
 *     removed — the photo IS the night sky.  The composite is re-assigned AFTER
 *     `engine.init(app)` so it wraps the SAME memoized node the engine bake uses.
 *   - Exports module-level `uSunDir` and `uNightFactor` uniforms that other
 *     modules (weather/clouds) import and read from their own TSL graphs, and
 *     `currentTod()` — the override-aware clock LightingModule dims by.
 *   - A RenderSub/priority-5 system updates the uniforms each frame from the
 *     same time-of-day source as the engine-sky `sunDirectionProvider`.
 *
 * CRITICAL (r182 rule): ALL TSL primitives (`uniform`, `vec3`, node `.add`/
 * `.mul`) are imported from `three/tsl`, NOT off the `three/webgpu` namespace.
 * `(THREE as any).foo` is `undefined` at runtime and breaks `vite build`.
 */

import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { SkyModule as EngineSky } from 'enginex/render/sky/SkyModule.js';
import { uniform, vec2, vec3, vec4, select, positionWorldDirection } from 'three/tsl';
import { buildCirrusLayer, cirrusCoverageFor } from '../lib/sky/cirrus_layer.js';
import {
  SheetRuntime, buildCloudSheets, createSheetTextures, createSheetUniforms, driveSheets, sheetsForWeather, sheetQuality, sheetFetches,
  setSheetFlash, sheetFlashRadiance, bakeSheetNoise, cloudSheetsMode, normalizeSheetSpec, sheetDaySeed, type SheetSpec,
} from '../lib/sky/cloud_sheets.js';
import { cloudKey } from '../lib/sky/cloud_light.js';
import { strokeEnvelope, strokeTimes, FLASH_S } from '../lib/vfx/lightning.js';
import { EMITTER_NITS, EMITTER_DISPLAY_MAX, emitterScene } from '../lib/vfx/fire_rules.js';
import { cloudGenus } from '../lib/stage4_fx.js';
import { Phase, Sub, makeSystem } from '../engine-compat.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { sunDirection, nightFactor, stepTod, flatSkyFallbackColor, sunObserver } from '../lib/lighting.js';
import { buildNightComposite } from '../lib/sky/night_composite.js';
import { NIGHT_DOMES, writeNightSkyUniforms } from '../lib/sky/night_sky.js';
import { UNIT_NITS, naturalSkyLift, nelmFromUnits, sqmFromUnits, NATURAL_DISPLAY_GAIN, setNaturalDisplayGain } from '../lib/sky/night_sky_model.js';
import { skyglowLive } from '../lib/light_pollution_live.js';
import { GLOW_TINT, NATURAL_SQM, nelmFromSqm, type LightDome, type Skyglow } from '../lib/light_pollution.js';
/** No artificial light (outside the Commons): the rain dome's deck is the natural share alone. */
const NO_SKYGLOW: Skyglow = { ratio: 0, sqm: NATURAL_SQM, bortle: 1, nelm: nelmFromSqm(NATURAL_SQM), tint: [...GLOW_TINT] as [number, number, number] };
import type { StarFieldInput } from '../lib/sky/star_field.js';
import { parseStarCatalog, bucketStars, STAR_GRID } from '../lib/sky/star_catalog.js';
import { stationAt, ambientSatsAt, meteorAt, battleSkyAt, battleSkyInputs, NO_BATTLE_INPUTS, type BattleSkyInputs, type Vec3, type SkyObserver, BATTLE_PERIOD_S } from '../lib/night_sky_schedule.js';
// space campaign L (2026-10-01): the ground reads the server's ONE war summary (`campaign_sky`) — the lever-off path keeps the old reduce.
import { warSkyNow, battleInputsFromWarSky, warSkyFeedState, overrideWarSky } from '../lib/war_sky_feed.js';
import { spaceComfortOn } from '../lib/space_comfort.js';
import { silhouetteBudget } from '@shared/war_sky.js';
import { Texture, TextureLoader, SRGBColorSpace, DataTexture, RGBAFormat, FloatType, NearestFilter } from 'three/webgpu';
import { KTX2Loader } from 'three/addons/loaders/KTX2Loader.js';
import { activePreset, activeTier } from '../lib/quality_tiers.js';
import { lightFrame, exposureState, emissiveEvScale, overcastZenithRadiance, horizonFade, E0_LUX, GROUND_ALBEDO, lum709, emissiveNits, keyLuminanceNits, EMISSIVE_MAX_OVER_KEY_EV, MOON_CHROMA, MOON_DISC_DEG, MOON_DISC_NITS, MOON_DISC_MAX_OVER_KEY_EV } from '../lib/photometry.js';
import { skySampler, nightSkyRadiance, nightSkyLive } from '../lib/sky/sky_sampler.js';
import { nightBakeIrradianceCpu, type NightBakeParams } from 'enginex/render/sky/lut/skyViewNode.js';
import type { SkyBakeNight } from 'enginex/render/sky/SkyRenderer.js';
import { bakedNightIrradianceScene, liveNightLux } from '../lib/sky/night_ibl.js';
import { ATLAS_ATMOSPHERE } from '../lib/sky/atmosphere.js';
import { type SkySig, type CubeSig, type CubeVerdict, type BakePolicyIn, SETTLE_FRAMES, envScale, cubeScaleOf, skyChanged, cubeVerdict, slewScale, envIntensityOf, bakeAction } from '../lib/sky/env_carry.js';
const cirrusSampler = skySampler();
import type { DbConnection } from '../module_bindings/index.js';

// ── Shared uniforms (other modules import these) ──────────────────────────────
/** World-space unit vector FROM origin TO the sun (updated every RenderSub frame). */
export const uSunDir = uniform(vec3(0, 1, 0));
/** 0 = full day, 1 = full night.  Gates the photographic night-sky layer. */
export const uNightFactor = uniform(0);

// ── Living night sky (2026-08-27): uniform slots the CPU scheduler drives ────
// (lib/night_sky_schedule.ts computes WHERE everything is from the shared wall
// clock; lib/sky/night_live.ts draws it. See those headers.)
const uMovers = [0, 1, 2].map(() => ({ dir: uniform(vec3(0, -1, 0)), bright: uniform(0) }));
const uMeteor = { dir: uniform(vec3(0, -1, 0)), vel: uniform(vec3(1, 0, 0)), len: uniform(0), bright: uniform(0) };
const uBattle = { dir: uniform(vec3(0, -1, 0)), tangent: uniform(vec3(1, 0, 0)), active: uniform(0), ships: uniform(1), fire: uniform(1) }; // + space campaign L: ships / fire
/** Live-layer master gain (dev/probe tuning + the pixel-diff proof harness). */
const uLiveGain = uniform(1);
/** Milky Way gain and the point-star gain — probe A/B levers (1 = the night_sky_model calibration; 2026-10-06 — the old
 *  1.15 display-anchored gains are gone with the display anchor). */
const uBgGain = uniform(1);
const uStarGain = uniform(1);
/**
 * THE NIGHT SKY (the night-sky realism wave, 2026-10-06 — lib/sky/night_sky.ts + night_sky_model.ts): the natural sky
 * (never black), the artificial sky from `skyglowLive()` (the zenith glow + the K = 4 light domes) and the stars'
 * visibility by the LOCAL limiting magnitude, all in sky units × `unitScene` (UNIT_NITS × luxToScene, written every
 * frame — THE PHYSICAL-EMISSIVE CONTRACT). `uStarCap` = the emissive cap over the key, in sky units.
 */
const uNightSky = {
  sunDir: uSunDir, unitScene: uniform(UNIT_NITS), lift: uniform(25), glow: uniform(vec4(0, ...GLOW_TINT)),
  domes: Array.from({ length: NIGHT_DOMES }, () => uniform(vec4(0, 0, 0, 1))),
  domeTints: Array.from({ length: NIGHT_DOMES }, () => uniform(vec3(...GLOW_TINT))),
  domeHeight: uniform(vec4(0.1, 0.1, 0.1, 0.1)), mwGain: uBgGain,
  // THE NIGHT DISPLAY GAIN (night_sky_model.NATURAL_DISPLAY_GAIN): every display term of the night sky together.
  natGain: uniform(NATURAL_DISPLAY_GAIN),
  // THE DECK (2026-10-06): the cloudiness over the camera — the lit town's light reflected back down (night_sky.ts).
  deck: uniform(0),
};
const uStarCap = uniform(1e5);
/** Probe / bed levers: a pinned skyglow (null = live), a lift multiplier. */
let nightSkyOverride: { glow: { ratio: number; tint: [number, number, number] }; domes: LightDome[] } | null = null;
let nightLiftScale = 1;
const nightSkyModelState = { lift: 0, liftScale: 1, unitScene: 0, capUnits: 0, glowRatio: 0, sqmZenith: 22, nelmZenith: 0, domes: 0, source: 'live' as 'live' | 'override' | 'none', moonGlobalLux: 0, deck: 0 };
/** The night layer's exposure anchor (emissiveEvScale(0) — the traffic and battle only now) and the deck's cover over it (1 = clear). */
const uNightExposure = uniform(1);
const uNightClear = uniform(1);
/** THE MOON in the sky (2026-09-27, sky/post P2 — lib/sky/moon_layer): the disc + the moonlit sky, scene units, driven by drivePhysicalSky. */
const uMoon = {
  dir: uniform(vec3(0, -1, 0)), sunDir: uSunDir, radiance: uniform(vec3(0, 0, 0)), sinRadius: uniform(Math.sin((MOON_DISC_DEG / 2) * Math.PI / 180)),
  skyZenith: uniform(vec3(0, 0, 0)), skyHorizon: uniform(vec3(0, 0, 0)), aureole: uniform(vec3(0, 0, 0)),
};
const moonSkyState = { discNits: 0, discScene: 0, skyZenith: 0, aureole: 0, elevationDeg: 0 };
// THE MOON WASHES THE SKY (sky/post P2, 2026-09-27) through the threshold model since 2026-10-06: the moonlit sky's
// luminance (buildMoonSky) raises the stars' limiting magnitude and drowns the Milky Way like any bright sky
// (lib/sky/night_sky.ts) — the fixed ×1/(1 + 9w) / ×1/(1 + 2w) washes are gone.
/** Sun disc radiance scale: the real disc is E/Ω ≈ 1.4·10⁴ × E; ~1000 keeps its fp16 pre-exposed radiance under ~8·10³ at every elevation (P2 lifts the cap). */
const SUN_DISC_SCALE = 1000;
/** What the physical sky pushed last (`__skyLight()`). */
const skyLightState = {
  radianceScale: 0, discScale: 0, overcastMix: 0, bakeOvercast: 0, envIntensity: 1, lastBakedScale: 0, cloudBucket: -1, rebakes: 0, cirrus: false, cirrusCoverage: 0, sheets: false,
  // THE LATCH LAW (lighting 3b, 2026-09-27 — lib/sky/env_carry): the carrier's inputs and the bake policy's last word.
  settledFrames: 0, verdict: 'regime' as string, target: 0, cubeScale: 0, carryScale: 0, lastAction: 'none' as string,
  immediates: 0, restarts: 0, requests: 0, entryPending: true, bake: null as unknown,
  /** THE LIT RAIN DECK: the town's drawn deck light added to the rain dome's zenith (cd/m²-equivalent, green). */
  rainDeckArt: 0,
  /** THE DECK'S NATURAL SHARE: the lifted natural sky the deck leaves, added to the rain dome's zenith (cd/m²-equivalent, green). */
  rainDeckNat: 0,
  /** THE BAKED NIGHT: the night ambient (lux) the cube is asked to carry this frame. */
  bakedNightLux: 0,
  /** THE LIVE NIGHT: the night lux the live cube delivers (lightFrame.nightIblLive). */
  nightIblLive: 0,
};
/** The high étage's uniforms (lib/sky/cirrus_layer): driven by drivePhysicalSky. */
const uCirrus = {
  camAbs: uniform(vec3(0, 0, 0)), height: uniform(8500), time: uniform(0), wind: uniform(vec2(1, 0)), coverage: uniform(0.25),
  sunDir: uSunDir, sunLight: uniform(vec3(0, 0, 0)), ambient: uniform(vec3(0, 0, 0)), horizon: uniform(vec3(0, 0, 0)),
};
/** The high cloud layer is a boot-time graph (next world entry): on wherever the tier has clouds, unless switched off. */
function highCloudsOn(): boolean { const p = activePreset(); return p.highClouds ?? !!p.cloudsEnabled; }
/**
 * THE LAYER CLOUDS (the cloud-realism work, 2026-10-07 — lib/sky/cloud_sheets): three plane-projected étages (cirrus /
 * cirrocumulus / cirrostratus · altocumulus / altostratus · stratocumulus / stratus / nimbostratus) composited over the
 * sky wherever the high-cloud switch is on. `atlas:cloud-sheets = 'legacy'` keeps the single cirrus sheet above.
 * `__cloudSheets(spec | null)` pins a state (genus + cover per étage) for probes and reports the live one.
 */
const uSheets = createSheetUniforms();
const sheetRt = new SheetRuntime();
let sheetPin: SheetSpec | null = null;
let sheetPinSnap = false;
/** The spec the sheets were last driven toward (the weather rule's, or the lever's pin) — `SkyModule.cloudSheetSpec`. */
let sheetSpecNow: SheetSpec | null = null;
const sheetState = { mode: 'off' as 'off' | 'sheets' | 'legacy', tier: '', fetches: null as unknown, drive: null as unknown, flash: 0 };
/** The last storm strike (the low deck's flash): its point relative to the eye, its stroke times, its age. */
const sheetStrike = { live: false, relX: 0, relZ: 0, t: 0, times: [] as number[], km: 0 };
/** What the star catalogue became on this client (`__nightStars()`). */
let nightStarsState: Record<string, unknown> = { count: 0, entries: 0, error: null };
const setV3 = (un: { value: unknown }, v: Vec3): void =>
  (un.value as { set: (x: number, y: number, z: number) => void }).set(v.x, v.y, v.z);
/** The latch law: a bake's overrides as the carrier's cube signature, into `out` (null when the bake carries no tag). */
function readCube(o: { radianceScale: number; tag?: number; sunY?: number; cloud?: number } | null, out: CubeSig): CubeSig | null {
  if (!o || o.tag === undefined) return null;
  out.rs = o.radianceScale; out.tag = o.tag; out.sunY = o.sunY; out.cloud = o.cloud;
  return out;
}
/** Probe mirror (__nightSky) — the last schedule the uniforms were driven to. */
const nightSkyState = { nightFactor: 0, station: 0, sats: [0, 0], satY: [0, 0], meteor: 0, battle: 0, battleDirY: 0, battleFort: '', battleSource: 'quiet', battleEndS: 0, ceasefire: false, warRows: 0,
  // space campaign L: the counted war (the server summary), the diurnal fold
  warCounted: false, warFolded: false, warCaps: null as number[] | null, warState: '' };

// ── The war the sky reads (2026-09-22, "the sky reads neither civ_treaty nor the campaign row") ──
// SpaceModule subscribes these two tables only once the player has ENTERED the sector (its first
// activate()); a grounded player has no rows. The sky takes its own one-shot subscription to the two
// small tables (treaties: a handful; campaigns: one an hour, reaped after a week) and reduces them
// once a second through the pure `battleSkyInputs` — the same reading `SpaceModule.ceasefireStands`
// and `campaignRow()` make, so the ground and the sector agree on whether a war exists and when it ends.
let warSkySubbed = false;
let warSkyInputs: BattleSkyInputs = NO_BATTLE_INPUTS;
let warSkyReadAt = 0;
let warSkyRows = 0;
/** Exported (2026-09-22): the night-sky bodies' cruiser mesh reads the SAME inputs as the shader
 *  layer — it used the bare clock and flew on through ceasefires and resolved campaigns. */
export function warInputsFor(nowS: number): BattleSkyInputs {
  const conn: DbConnection | null = atlas.conn;
  // Space campaign L (2026-10-01): THE ONE READING — this cycle's server-written summary (`campaign_sky`, ≤ 1 KiB): a
  // ceasefire row is no war, any other row ends the war at its own end. `atlas:space-comfort-v1 = 'off'` restores the
  // old reduce of civ_treaty + space_campaign below.
  if (spaceComfortOn()) {
    const s = warSkyNow(conn, nowS, (t) => Math.floor(t / BATTLE_PERIOD_S));
    warSkyRows = warSkyFeedState().rows;
    return battleInputsFromWarSky(s);
  }
  if (!conn) return warSkyInputs;
  if (!warSkySubbed) {
    warSkySubbed = true;
    try { conn.subscriptionBuilder().subscribe(['SELECT * FROM civ_treaty', 'SELECT * FROM space_campaign']); } catch { /* pre-publish */ }
  }
  if (nowS - warSkyReadAt < 1) return warSkyInputs;
  warSkyReadAt = nowS;
  try {
    const cyc = Math.floor(nowS / 3600); // the cycle id is the mirror's (BATTLE_PERIOD_S); battleSkyAt re-derives it
    const db = conn.db as unknown as {
      civTreaty: { iter(): Iterable<{ state: string; fromCyc: bigint; untilCyc: bigint }> };
      spaceCampaign: { iter(): Iterable<{ cyc: bigint; state: string; endsAt: { microsSinceUnixEpoch: bigint } }> };
    };
    const treaties: Array<{ state: string; fromCyc: number; untilCyc: number }> = [];
    for (const tr of db.civTreaty.iter()) treaties.push({ state: tr.state, fromCyc: Number(tr.fromCyc), untilCyc: Number(tr.untilCyc) });
    const campaigns: Array<{ cyc: number; state: string; endsAtS: number }> = [];
    for (const c of db.spaceCampaign.iter()) campaigns.push({ cyc: Number(c.cyc), state: c.state, endsAtS: Number(c.endsAt.microsSinceUnixEpoch / 1_000_000n) });
    warSkyRows = treaties.length + campaigns.length;
    warSkyInputs = battleSkyInputs(cyc, treaties, campaigns);
  } catch { /* rows mid-churn or the tables not yet published — keep the last reading */ }
  return warSkyInputs;
}

/** Space campaign L: where the observer stands in the sky's day — the war rides the planet's rotation (THE DIURNAL FOLD);
 *  null with the lever off (the fixed landmark bearing). */
export function warObserver(): SkyObserver | null {
  return spaceComfortOn() ? { tod: currentWorldTimeOfDay(), latDeg: sunObserver().latDeg } : null;
}
/** Space campaign L: this cycle's war summary for the counted silhouettes (null with the lever off or no war row). */
export function warSkySummaryNow(nowS: number): ReturnType<typeof warSkyNow> {
  return spaceComfortOn() ? warSkyNow(atlas.conn, nowS, (t) => Math.floor(t / BATTLE_PERIOD_S)) : null;
}

// ── SPACE HANDOVER (space agent, 2026-09-25 — report §9 R1/R2) ─────────────────
// The sector owns the background and pauses the ground sky's LUT / PMREM bakes while it flies (they kept
// re-baking a Commons sky nobody sees, and each bake re-assigned scene.environment over the space IBL).
// SpaceModule calls these at activate / deactivate only. The ground node a late asset load composes while
// the sector holds the background is PARKED, never assigned over the space sky.
let skyBakePaused = false;
let groundBgSaved: { node: unknown; bg: unknown } | null = null;
let nightAssets: { bg: Texture | null; stars: StarFieldInput | null } | null = null;
/** Pause (true) / resume the engine sky's per-frame LUT + PMREM bake driver. */
export function setSkyBakePaused(on: boolean): void { skyBakePaused = on; }
export function skyBakeIsPaused(): boolean { return skyBakePaused; }
/** Hand the scene background to the sector (a node) or give it back (null). */
export function setSpaceBackground(scene: { backgroundNode?: unknown; background: unknown }, node: unknown | null): void {
  if (node) {
    if (!groundBgSaved) groundBgSaved = { node: scene.backgroundNode ?? null, bg: scene.background };
    scene.backgroundNode = node;
  } else if (groundBgSaved) {
    scene.backgroundNode = groundBgSaved.node;
    scene.background = groundBgSaved.bg;
    groundBgSaved = null;
  }
}
/** The night photo's starless background + the bucketed star catalogue, once the ground sky loaded them (null on the flat tier / before). */
export function nightSkyAssets(): { bg: Texture | null; stars: StarFieldInput | null } | null { return nightAssets; }
// ── END SPACE HANDOVER ──

// ── Local time-of-day override (dev/admin) ────────────────────────────────────
/**
 * When non-null, `currentWorldTimeOfDay()` returns this instead of the live
 * `skyState` clock — a VIEW-ONLY override (the admin debug menu's Local mode +
 * the preview keys). It drives the sun, clouds, and the night-sky gates in this
 * client only; it never mutates shared state. null = follow the live clock.
 */
let debugTodOverride: number | null = null;
if (typeof window !== 'undefined') (window as unknown as Record<string, unknown>).__setTod = (v: number | null): void => setDebugTod(v);
/** Debug menu (Local mode) / preview keys: set the view-only time-of-day. null = clear → live clock. */
export function setDebugTod(v: number | null): void {
  debugTodOverride = v;
  // Probe lever (headless screenshots want daylight): `__setTod(0.5)` / `__setTod(null)`.
  (window as unknown as Record<string, unknown>).__setTod = setDebugTod;
  // Fog reads the tod band event-driven (AtmosphereModule.applyFromSky), so a
  // preview jump must ping it — otherwise `\` shows a midnight sky with noon fog.
  bus.emit('atlas:environment-updated', {});
}

// ── Per-world clock helper ────────────────────────────────────────────────────
/**
 * Returns the time-of-day (0..1) for the CURRENT world the local avatar is in.
 *
 * Priority:
 *   0. `debugTodOverride` (dev/admin Local override) when set — wins over all.
 *   1. `atlas.currentWorldId` → `skyState.worldId.find(wid)?.timeOfDay`
 *   2. commons world's `skyState` (mirror of `LightingModule.commonsTimeOfDay`)
 *   3. 0.5 (midday fallback when the DB is not yet connected)
 */
function currentWorldTimeOfDay(): number {
  if (debugTodOverride !== null) return debugTodOverride; // dev/admin Local time override (debug menu + preview keys)
  const conn: DbConnection | null = atlas.conn;
  if (!conn) return 0.5;

  // 1. Current world the player is in.
  const wid = atlas.currentWorldId;
  if (wid != null) {
    const s = conn.db.skyState.worldId.find(wid);
    if (s) return s.timeOfDay;
  }

  // 2. Commons world fallback — O(1) via the resolved id (bug sweep: this
  // walked the ENTIRE world table, one row per personal world, three-plus
  // times per frame whenever the current world had no sky row). When the id
  // is not resolved yet (boot, synthetic harnesses), the old iteration is
  // the correctness fallback — rare, so the per-frame cost stays gone.
  if (atlas.commonsWorldId !== null) {
    const s = conn.db.skyState.worldId.find(atlas.commonsWorldId);
    if (s) return s.timeOfDay;
  } else {
    for (const w of conn.db.world.iter()) {
      if (w.kind === 'commons') {
        const s = conn.db.skyState.worldId.find(w.worldId);
        if (s) return s.timeOfDay;
      }
    }
  }

  return 0.5;
}

/**
 * The time-of-day the sky is rendering RIGHT NOW (live world clock or the
 * debug/preview override).  LightingModule reads this every frame so the sun
 * arc + ambient level always match the VISIBLE sky — previewing midnight via
 * the `\` key dims the world exactly like real midnight.
 */
export function currentTod(): number { return currentWorldTimeOfDay(); }

// ── Atlas SkyModule ───────────────────────────────────────────────────────────
export class SkyModule implements IModule {
  /** Distinct name — the engine SkyModule uses 'Sky'. */
  readonly name = 'AtlasSky';

  private _engineSky: EngineSky | null = null;
  /** Boot loading screen: true once the night photo reached a terminal state
   *  (KTX2 loaded, webp fallback loaded, or both failed → atmosphere-only). */
  private _photoResolved = false;
  get ready(): boolean { return this._flatReady || (this._engineSky !== null && this._photoResolved); }
  /**
   * THE LAYER CLOUDS' live spec (2026-10-07): the genus and cover per étage the sheets are being driven TOWARD this frame
   * — the weather rule's choice, or the `__cloudSheets` lever's pin. null while the sheets are not the sky (the flat
   * tier, the high-cloud switch off, `atlas:cloud-sheets = 'legacy'`) and before the first lit frame. Written at
   * RenderSub/5; the drawn state eases toward it over ~5 s (`cloudSheetShown`). Not refreshed in space flight.
   */
  get cloudSheetSpec(): SheetSpec | null { return sheetState.mode === 'sheets' ? sheetSpecNow : null; }
  /** What is DRAWN right now (the eased genus and cover per étage), or null as above. */
  get cloudSheetShown(): { high: { genus: string; cover: number }; mid: { genus: string; cover: number }; low: { genus: string; cover: number } } | null {
    return sheetState.mode === 'sheets' && sheetSpecNow ? sheetRt.state().shown : null;
  }
  /**
   * Stored keydown handler for cleanup in dispose().
   * Added at init time so the night sky is instantly inspectable via dev keys.
   */
  private _keyHandler: ((e: KeyboardEvent) => void) | null = null;

  /** MINIMAL sky (2026-09-08): a flat background the colour of the fog. */
  private flat: THREE.Color | null = null;
  private _flatReady = false;
  private _flatSource: 'atmosphere' | 'scene.fog' | 'fallback' = 'fallback';

  /**
   * The flat sky of the minimal tier: no atmosphere, no LUTs, no IBL bake, no
   * night photo, no living sky — `scene.background` is a colour copied from
   * the fog every frame, so the fog wall and the sky are one surface. uSunDir
   * and the night factor still track the hour (water, lights and fog read them).
   */
  private initFlat(app: App): void {
    const scene = app.world.scene as unknown as { background: unknown; backgroundNode?: unknown; fog?: { color?: THREE.Color } | null };
    this.flat = new THREE.Color(0x8fa8c0);
    scene.backgroundNode = null;
    scene.background = this.flat;
    this._photoResolved = true;
    this._flatReady = true;
    app.world.addSystem(makeSystem('AtlasSkyUniforms', Phase.RenderSub, 5, () => {
      const d = sunDirection(currentWorldTimeOfDay());
      (uSunDir.value as { set: (x: number, y: number, z: number) => void }).set(d.x, d.y, d.z);
      const nf = nightFactor(d.y);
      (uNightFactor as unknown as { value: number }).value = nf;
      nightSkyState.nightFactor = nf;
      // THE ALWAYS-BLUE SKY (2026-09-09, "the sky is always blue … it is
      // midnight"): this copied `scene.fog.color`, so whenever the fog was
      // detached from the scene (the probe light rig, space, a personal
      // preset's restore, a world script's fog swap) the colour froze at the
      // boot blue for the rest of the session. Copy the ATMOSPHERE MODULE's
      // own fog colour instead — it tracks the visible time-of-day band and
      // the weather whether or not the scene carries it — and fall back to a
      // pure day/night ramp when the module is absent; re-assert the
      // background object each tick so a restore path can never swap it out.
      const atmo = app.getModule('Atmosphere') as { fogColor?: THREE.Color } | undefined;
      const src = atmo?.fogColor ?? scene.fog?.color ?? null;
      if (this.flat) {
        if (src) this.flat.copy(src); else flatSkyFallbackColor(nf, this.flat);
        if (!scene.backgroundNode && scene.background !== this.flat) scene.background = this.flat;
      }
      this._flatSource = src ? (atmo?.fogColor ? 'atmosphere' : 'scene.fog') : 'fallback';
    }, Sub.Update));
    (globalThis as unknown as { __sky?: () => unknown }).__sky = () => ({ mode: 'flat', color: this.flat ? `#${this.flat.getHexString()}` : null, source: this._flatSource, night: nightSkyState.nightFactor });
  }

  init(app: App): void {
    if (activePreset().skyQuality === 'flat') { this.initFlat(app); return; }
    (globalThis as unknown as { __sky?: () => unknown }).__sky = () => ({ mode: 'full' });
    // Build and register the engine SkyModule.  It will:
    //   • create SkyRenderer + set scene.backgroundNode (memoized)
    //   • set scene.background = null
    //   • create SkyEnvironmentBaker
    //   • register SkyRuntimeSystem at RenderSub/priority 6 (calls
    //     sunDirectionProvider each frame + re-bakes PMREM → scene.environment)
    this._engineSky = new EngineSky({
      sunDirectionProvider: () => sunDirection(currentWorldTimeOfDay()),
      pipeline: 'auto',           // 4-LUT on WebGPU, inline ray-march on WebGL2
      // lighting (2026-09-26): the WebGL2 march adds the LUT dome's Ψ_ms + disc — single scattering alone was 4.3× darker
      // than the LUT sky on the 1° ring the fog colour is cut from (the bright band where the fogged far tier met it).
      lutParity: true,
      // Aerial-perspective froxel bake is CPU-heavy per re-bake, so it only
      // runs when the active graphics tier wants the composite (PostModule
      // wires the volume into the post chain). Boot-time gate: a runtime tier
      // change picks it up on the next reload (the menu says so). LUT-path
      // only — on WebGL2's inline sky there is no LUT set and no volume.
      enableAerialPerspective: activePreset().aerialEnabled,
      // FIX (shader sweep C, 2026-10-06; agent A's request): the boot bake takes the frame PostModule composites with —
      // the engine default 'screen' baked a volume PostModule then threw away for its 'world' default (a wasted bake at
      // boot). Mirrors PostModule.aerialFrame: 'world' unless `atlas:aerial-frame` = 'screen'.
      aerialFrame: ((): 'screen' | 'world' => { try { return localStorage.getItem('atlas:aerial-frame') === 'screen' ? 'screen' : 'world'; } catch { return 'world'; } })(),
      // ONE PHOTOMETRY TABLE (2026-09-25, lib/photometry): the LUTs bake PER UNIT E0
      // (sunIlluminance 1) and the sky node's `radianceScale` carries E0 × the
      // exposure curve's lux→scene factor every frame — the SAME numbers the key
      // light is made of, so sky and ground can no longer disagree (the old 2.0
      // was a fourth, unrelated brightness knob).
      sunIlluminance: 1.0,
      // ATLAS_ATMOSPHERE (2026-09-27): the visibility-derived aerosol column (lib/sky/atmosphere) — the SAME config the
      // CPU sampler bakes, so the sky drawn and the light cast are one air. Hillaire's table has no aerosol.
      atmosphere: ATLAS_ATMOSPHERE,
      // A 2 h day cycle moves the sun ~0.07° in 5 s — re-baking the IBL every 5 s (the
      // default) was a periodic PMREM render-target realloc HITCH that read as "lag while
      // moving" (a frame stutter is invisible on a still image, visible in motion). 60 s
      // + the engine's own sun-drift re-bake keeps the IBL current with far fewer hitches.
      envBakeMaxInterval: 60,
      // Diffuse IBL needs no 128² cube; 64² quarters each bake's cost → a smaller hitch.
      envResolution: 64,
      // Ground-scale game on ±320 m relief: the real sky barely changes over a
      // few hundred meters, but tracking the camera into the LUT observer
      // height re-baked the sky-view LUT in DISCRETE steps as you climbed — a
      // visible sky "pop" per threshold plus CPU re-bakes mid-traversal.
      trackCameraAltitude: false,
    });
    this._engineSky.init(app);
    // ── SPACE HANDOVER (space agent, 2026-09-25): gate the engine's SkyRuntime bake driver on the pause flag. ──
    {
      const sys = (this._engineSky as unknown as { _system?: { execute(dt: number, w: unknown): void } })._system;
      if (sys) {
        const run = sys.execute.bind(sys);
        sys.execute = (dt: number, w: unknown): void => { if (!skyBakePaused) run(dt, w); };
      }
    }
    // ── END SPACE HANDOVER ──
    // THE LATCH LAW (lighting 3b, 2026-09-27): a bake cycle latches its values at the first face — only from a frame
    // whose sky has held still (a time pin lands a frame before the snapped weather); a world entry or a reload starts
    // from a bake of the CURRENT frame (drivePhysicalSky asks for the one-shot).
    this._engineSky.setEnvBakeGate(() => this.settledFrames >= SETTLE_FRAMES);
    for (const ev of ['atlas:world-entered', 'atlas:loading-done'] as const) bus.on(ev, () => this.markEntry());

    // World-space view ray for the photo night-sky layer.
    //
    // WHY `positionWorldDirection` (two bugs led here):
    //  1. "night sky shifts when you WALK": the background skybox is a UNIT sphere
    //     with an IDENTITY matrixWorld drawn camera-locked at the far plane (three
    //     `common/Background.js`, w=0 model-view), so `positionWorld` is a unit
    //     DIRECTION — the original `normalize(positionWorld - cameraPosition)`
    //     subtracted a metres-scale camera position from it and the star field
    //     parallaxed with translation.
    //  2. "night sky moves when you LOOK UP/DOWN": the interim fix reconstructed the
    //     ray from `screenUV` + `cameraProjectionMatrixInverse` + `cameraWorldMatrix`
    //     (w=0). But screenUV's vertical origin is a backend/context convention —
    //     in the WebGPU background-mesh context the reconstruction came out
    //     vertically flipped, so the sampled sky direction mis-tracked camera PITCH
    //     and the stars dragged with vertical look (yaw looked fine, which is why
    //     the flip survived review).
    //
    // `positionWorldDirection` is the background sphere's OWN per-fragment world
    // direction: translation-invariant by construction, correct under all camera
    // rotation on both backends (no screen-space conventions involved), and the
    // SAME ray the engine atmosphere uses since the SkyRenderer/skyViewNode fix —
    // the two layers stay aligned at every camera pose.
    const viewDir = positionWorldDirection;

    // Composite background node (linear HDR, upstream of bloom + the ACES tonemap):
    //   atmosphere = the memoized node the engine already assigned + bakes IBL from
    //   composite  = atmosphere + photoNight·nightFactor
    //
    // The night sky is the Higgsfield astrophoto SPLIT IN TWO (2026-09-11): a
    // starless 4K equirect for the Milky Way glow and a catalogue of its stars
    // drawn as points (see below). Both sample by WORLD direction
    // (`positionWorldDirection`, see above), so the sky is pinned to the WORLD —
    // turn or pitch your head and the constellations stay put; walk and they
    // never parallax (stars are at infinity).
    //
    // NOTE: do NOT .add(sunDisc) — the 4-LUT path (WebGPU) includes an analytic
    // sun disc inside buildBackgroundNode().  Adding a second disc would
    // double-draw the sun.
    const atmosphereNode = this._engineSky.renderer.buildBackgroundNode();
    // The night sky is TWO assets (2026-09-11, extract-sky-stars.mjs): a 4K
    // STARLESS background equirect (KTX2/UASTC first — PRE-FLIPPED vertically
    // like every sky KTX2, compressed textures can't honour flipY at upload —
    // with the WebP as the fallback) and a STAR CATALOGUE drawn as round
    // points on the sphere (lib/sky/star_field.ts): resolution-independent
    // and free of the equirect pole squeeze that turned the 8K photo's stars
    // into radial ellipses at the zenith. One background for every tier
    // (4 MB); the tier only sets the per-cell loop cap. The composite is
    // (re)assigned when both land — TSL binds the texture OBJECT at build
    // time; until then the background is atmosphere-only.
    const maxPerCell = activePreset().memoryBudgetMb >= 256 ? 64 : 32;
    const applySky = (bgTex: Texture | null, stars: StarFieldInput | null): void => {
      // THE NIGHT SKY (2026-10-06): lib/sky/night_composite.ts builds atmosphere + night + moon in one Fn (the bed draws
      // the same graph).
      const skyNight = buildNightComposite(viewDir, atmosphereNode, bgTex, stars, {
        nightFactor: uNightFactor, nightClear: uNightClear, sky: uNightSky, starGain: uStarGain, starCap: uStarCap, moon: uMoon,
        live: { sunDir: uSunDir, gain: uLiveGain, exposure: uNightExposure, movers: uMovers, meteor: uMeteor, battle: uBattle },
      });
      // THE HIGH ÉTAGE (2026-09-25, §8 R6b): a plane-projected cirrus / cirrostratus sheet at 8.5 km, OVER the
      // sky and the stars (premultiplied), on tiers with clouds. Built once; everything it reads is a uniform.
      let groundNode: unknown = skyNight;
      // THE LAYER CLOUDS (2026-10-07): the three étages over the sky and the stars — one graph, every input a uniform, an
      // absent étage behind its own uniform branch. Slab samples by the tier's NAME (no tier default moved).
      if (highCloudsOn() && cloudSheetsMode() === 'sheets') {
        const q = sheetQuality(activeTier());
        const tex = createSheetTextures(THREE as never, bakeSheetNoise());
        groundNode = vec4(buildCloudSheets(viewDir, vec4(skyNight as never).rgb, tex, uSheets, q), 1);
        skyLightState.sheets = true;
        sheetState.mode = 'sheets'; sheetState.tier = activeTier(); sheetState.fetches = sheetFetches(activeTier());
      } else if (highCloudsOn()) {
        sheetState.mode = 'legacy';
        const c = buildCirrusLayer(viewDir, uCirrus);
        const base = vec4(skyNight as never).rgb;
        // PERF (shader sweep C, 2026-10-06): the sheet's rgb and alpha both carry smoothstep(0.005, 0.06, d.y), so at and
        // below d.y = 0.005 the layer is exactly (0, 0) — yet the seven fBm octaves ran on every pixel under the horizon
        // too (the background shades every pixel). One select yields (rgb, a) or zero; `base` is built OUTSIDE it so the
        // atmosphere is generated once. Per-pixel branch, no derivatives inside: legal on both backends.
        const cl = select(vec3(viewDir).y.greaterThan(0.005), vec4(c.rgb, c.a), vec4(0, 0, 0, 0));
        groundNode = vec4(base.mul(cl.a.oneMinus()).add(cl.rgb), 1);
        skyLightState.cirrus = true;
      }
      // SPACE HANDOVER (space agent, 2026-09-25): while the sector holds the background, park the ground node.
      if (groundBgSaved) groundBgSaved.node = groundNode;
      else (app.world.scene as unknown as { backgroundNode?: unknown }).backgroundNode = groundNode;
    };
    const loadBackground = new Promise<Texture | null>((resolve) => {
      const ktx = new KTX2Loader().setTranscoderPath('/basis/');
      ktx.detectSupport(app.renderer.native as never);
      ktx.load('/sky/night_sky_clear_bg.ktx2',
        (t) => { t.colorSpace = SRGBColorSpace; ktx.dispose(); resolve(t); },
        undefined,
        () => {
          ktx.dispose();
          new TextureLoader().load('/sky/night_sky_clear_bg.webp',
            (t) => { t.colorSpace = SRGBColorSpace; resolve(t); },
            undefined, () => resolve(null) /* both missing → atmosphere-only night IS the final state */);
        });
    });
    const loadStars: Promise<StarFieldInput | null> = fetch('/sky/night_sky_clear.stars.bin')
      .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((buf) => {
        const t0 = performance.now();
        const cat = parseStarCatalog(buf);
        const b = bucketStars(cat, STAR_GRID, maxPerCell);
        const mk = (data: Float32Array, w: number, h: number): DataTexture => {
          const t = new DataTexture(data, w, h, RGBAFormat, FloatType);
          t.minFilter = NearestFilter; t.magFilter = NearestFilter; t.generateMipmaps = false; t.flipY = false;
          t.needsUpdate = true;
          return t;
        };
        nightStarsState = {
          count: cat.count, entries: b.entries, occupied: b.occupied, maxInCell: b.maxInCell, capped: b.capped,
          grid: b.grid, maxPerCell, recordsHeight: b.recordsHeight, gpuBytes: (b.cells.length + b.records.length) * 4,
          bucketMs: Math.round(performance.now() - t0), error: null,
        };
        return { cells: mk(b.cells, b.cellsWidth, b.cellsHeight), records: mk(b.records, b.recordsWidth, b.recordsHeight), grid: b.grid, recordsWidth: b.recordsWidth, maxPerCell };
      })
      .catch((e: unknown) => { nightStarsState = { ...nightStarsState, error: String(e) }; console.warn('[AtlasSky] star catalogue unavailable:', e); return null; });
    Promise.all([loadBackground, loadStars]).then(([bg, stars]) => { nightAssets = { bg, stars } /* SPACE HANDOVER: the sector reuses them */; applySky(bg, stars); this._photoResolved = true; });
    const composite = atmosphereNode;

    // The background node is now PURELY the sky (atmosphere + night layers).
    // Phase C originally composited the volumetric clouds here, but the
    // background skybox is a unit sphere drawn camera-locked at the far plane
    // (Three.js `Background.js` uses a w=0 model-view transform so it "will not
    // translate when the camera moves"), so finite-altitude clouds (1200–3500 m)
    // composited into it read as a band PINNED to the view. The clouds moved to
    // a depth-aware POST pass (PostModule → engine PostProcessingPipeline
    // `clouds` hook) where the cloud node ray-marches from the REAL camera
    // position: world-anchored, parallaxing, and occluded by opaque geometry via
    // the sky-pixel depth mask. See PostModule.ts.
    const sceneAny = app.world.scene as unknown as { backgroundNode?: unknown };
    sceneAny.backgroundNode = composite;
    // Probe: the living-sky schedule as the uniforms last saw it (headless
    // harnesses assert determinism + activity without reading pixels).
    (globalThis as Record<string, unknown>).__nightSky = () => ({ ...nightSkyState });
    // Space campaign L: the war feed's probe lever — the feed's state, and a pinned summary (null = no war; undefined releases).
    (globalThis as Record<string, unknown>).__warSky = { state: () => warSkyFeedState(), override: (s: Parameters<typeof overrideWarSky>[0]) => { overrideWarSky(s); return warSkyFeedState(); } };
    (globalThis as Record<string, unknown>).__nightSkyGain = (v: number) => { (uLiveGain as unknown as { value: number }).value = v; return v; };
    (globalThis as Record<string, unknown>).__nightStars = () => ({ ...nightStarsState });
    (globalThis as Record<string, unknown>).__nightStarsGain = (v: number) => { (uStarGain as unknown as { value: number }).value = v; return v; };
    (globalThis as Record<string, unknown>).__nightBgGain = (v: number) => { (uBgGain as unknown as { value: number }).value = v; return v; };
    // THE NIGHT SKY (2026-10-06): its state (lift, NELM at the zenith, the source), a pinned skyglow and a lift multiplier.
    (globalThis as Record<string, unknown>).__nightSkyModel = () => ({ ...nightSkyModelState, starGain: (uStarGain as unknown as { value: number }).value, mwGain: (uBgGain as unknown as { value: number }).value });
    (globalThis as Record<string, unknown>).__nightSkyLive = (o: { glow: { ratio: number; tint?: [number, number, number] }; domes?: LightDome[] } | null) => {
      nightSkyOverride = o ? { glow: { ratio: o.glow.ratio, tint: o.glow.tint ?? [...GLOW_TINT] as [number, number, number] }, domes: o.domes ?? [] } : null;
      return { ...nightSkyModelState };
    };
    (globalThis as Record<string, unknown>).__nightSkyLift = (v: number | null) => { nightLiftScale = v == null ? 1 : Math.max(0, v); return nightLiftScale; };
    // The night display gain, moved live in the shader AND the CPU mirror (the fog reads nightSkyUnits) together.
    (globalThis as Record<string, unknown>).__nightSkyNatGain = (v: number | null) => { const g = setNaturalDisplayGain(v); (uNightSky.natGain as unknown as { value: number }).value = g; return g; };
    // THE LAYER CLOUDS' lever: `__cloudSheets({ high: { genus: 'cs', cover: 0.8 }, low: 'sc' })` pins a state at once
    // (a missing étage is absent), `__cloudSheets(null)` gives the weather back, no argument only reports.
    (globalThis as Record<string, unknown>).__cloudSheets = (o?: Parameters<typeof normalizeSheetSpec>[0] | null) => {
      if (o !== undefined) { sheetPin = o === null ? null : normalizeSheetSpec(o); sheetPinSnap = true; }
      return { mode: sheetState.mode, tier: sheetState.tier, pinned: sheetPin !== null, fetches: sheetState.fetches, flash: sheetState.flash, ...sheetRt.state(), drive: sheetState.drive };
    };
    // The storm's strike lights a patch of the low deck (WeatherModule fires the bus event on the flash's rising edge;
    // AtlasLightningModule places the bolt by the same bearing: x + sin·d, z + cos·d).
    bus.on('atlas:lightning-strike', (s) => {
      const d = Math.max(250, s.km * 1000);
      sheetStrike.live = true; sheetStrike.t = 0; sheetStrike.relX = Math.sin(s.bearing) * d; sheetStrike.relZ = Math.cos(s.bearing) * d;
      sheetStrike.times = strokeTimes(Math.floor(s.h * 1e6)); sheetStrike.km = s.km;
    });

    // RenderSub / priority 5 — runs BEFORE the engine bake at priority 6 so
    // the uniforms are current when the baker samples the sky-view LUT.
    app.world.addSystem(
      makeSystem(
        'AtlasSkyUniforms',
        Phase.RenderSub,
        5,
        (_dt, _world) => {
          const tod = currentWorldTimeOfDay();
          const d = sunDirection(tod);
          (uSunDir.value as { set: (x: number, y: number, z: number) => void })
            .set(d.x, d.y, d.z);
          const nf = nightFactor(d.y);
          (uNightFactor as unknown as { value: number }).value = nf;
          // ── The living sky: drive the traffic/battle uniforms from the
          // shared wall clock (a handful of hashes — negligible). Day skips
          // the traffic (invisible anyway) but zeroes the slots so a dusk
          // fade-in never pops a stale mover.
          nightSkyState.nightFactor = nf;
          if (nf > 0.001) {
            const nowS = Date.now() / 1000;
            const st = stationAt(nowS);
            const [sa, sb] = ambientSatsAt(nowS);
            setV3(uMovers[0].dir, st.dir); (uMovers[0].bright as { value: number }).value = st.bright;
            setV3(uMovers[1].dir, sa.dir); (uMovers[1].bright as { value: number }).value = sa.bright;
            setV3(uMovers[2].dir, sb.dir); (uMovers[2].bright as { value: number }).value = sb.bright;
            const me = meteorAt(nowS);
            setV3(uMeteor.dir, me.dir); setV3(uMeteor.vel, me.vel);
            (uMeteor.len as { value: number }).value = me.len;
            (uMeteor.bright as { value: number }).value = me.bright;
            const wi = warInputsFor(nowS);
            const bs = battleSkyAt(nowS, wi, warObserver()); // space campaign L: + the diurnal fold
            setV3(uBattle.dir, bs.dir); setV3(uBattle.tangent, bs.tangent);
            (uBattle.active as { value: number }).value = bs.active;
            // Space campaign L: with a summary row the COUNTED batch (AtlasNightSkyBodies) draws the ships, and the flashes
            // follow the living fleet; without one the one-cruiser picture stays.
            const ws = warSkySummaryNow(nowS);
            const counted = !!ws && ws.state !== 'ceasefire';
            (uBattle.ships as { value: number }).value = counted ? 0 : 1;
            if (counted) { const sb = silhouetteBudget(ws); (uBattle.fire as { value: number }).value = Math.min(1, (sb.caps[0] + sb.caps[1]) / 10 + (sb.gunships[0] + sb.gunships[1]) / 40); }
            else (uBattle.fire as { value: number }).value = 1;
            nightSkyState.warCounted = counted; nightSkyState.warFolded = !!bs.folded;
            nightSkyState.warCaps = ws ? [ws.caps[0], ws.caps[1]] : null; nightSkyState.warState = ws?.state ?? '';
            nightSkyState.station = st.bright; nightSkyState.sats = [sa.bright, sb.bright];
            nightSkyState.satY = [sa.dir.y, sb.dir.y]; // the MOVING quantity (probe aliveness)
            nightSkyState.meteor = me.bright; nightSkyState.battle = bs.active; nightSkyState.battleDirY = bs.dir.y;
            nightSkyState.battleFort = bs.fort; nightSkyState.battleSource = bs.source; nightSkyState.battleEndS = bs.endS;
            nightSkyState.ceasefire = wi.ceasefire; nightSkyState.warRows = warSkyRows;
          } else {
            (uMovers[0].bright as { value: number }).value = 0;
            (uMovers[1].bright as { value: number }).value = 0;
            (uMovers[2].bright as { value: number }).value = 0;
            (uMeteor.bright as { value: number }).value = 0;
            (uBattle.active as { value: number }).value = 0;
            nightSkyState.station = 0; nightSkyState.meteor = 0; nightSkyState.battle = 0; nightSkyState.battleSource = 'quiet';
          }
          this.drivePhysicalSky(app);
        },
        Sub.Update,
      ),
    );
    // THE CARRIER after the engine's bake step (SkyRuntime, RenderSub/6): see carryEnv.
    app.world.addSystem(makeSystem('AtlasSkyEnv', Phase.RenderSub, 7, (dt) => this.carryEnv(app, dt), Sub.Update));
    (globalThis as Record<string, unknown>).__skyLight = () => ({ ...skyLightState, moonSky: { ...moonSkyState } });

    // ── Time-of-day preview keys (dev affordance) ────────────────────────────
    // WHY: The day/night cycle runs on a 2-hour real-time clock. Without a
    // shortcut the night sky is only visible after ~30 minutes of waiting. These
    // keys drive the LOCAL view-only time override (`setDebugTod`) so a developer
    // can instantly inspect the night-sky composite in-browser.
    //
    //   \  (Backslash) → jump to deep night  (timeOfDay = 0.0 ≈ midnight)
    //   [              → step tod −0.05       (move backward toward midnight)
    //   ]              → step tod +0.05       (move forward toward noon)
    //
    // tod = 0.5 is noon (sun at apex); tod ≈ 0.0 / 1.0 is midnight (sun below
    // horizon → nightFactor + deepNight → 1).  Setting tod = 0.0 is therefore
    // "jump to deep night" — the most useful single-key shortcut for reviewers.
    // The admin debug menu's Day/Night section shares this same Local override.
    //
    // Guard: skip when an input/textarea is focused (don't hijack typing).
    // Guard: skip auto-repeat (keydown fires repeatedly while held — one fire
    // per physical press is enough for a dev tool).
    //
    // These keys now mutate ONLY this client's view (no `debugSetSkyTime`, no
    // shared-Commons write — the prior griefing vector is gone). The dev-origin
    // gate below is retained to scope the keyboard affordance to developers, not
    // as a security boundary: register the listener ONLY on a local/dev origin,
    // or when a developer on a deployed build opts in via localStorage.
    const _hostname = (typeof location !== 'undefined' ? location.hostname : '') || '';
    const _localOrigin =
      _hostname === 'localhost' ||
      _hostname === '127.0.0.1' ||
      _hostname === '[::1]' ||
      _hostname === '';
    let _optIn = false;
    try {
      _optIn =
        typeof localStorage !== 'undefined' &&
        localStorage.getItem('atlas:skyPreview') === '1';
    } catch {
      /* storage blocked (private-browsing / permissions policy) */
    }
    const _previewEnabled = _localOrigin || _optIn;

    if (!_previewEnabled) {
      // Leave _keyHandler null — dispose() already null-guards the removeEventListener.
      console.info(
        '[AtlasSky] Time-preview keys disabled on non-local origin. ' +
          "To enable: localStorage.setItem('atlas:skyPreview','1') and reload.",
      );
    } else {
      this._keyHandler = (e: KeyboardEvent): void => {
        // Skip auto-repeat keydown events.
        if (e.repeat) return;
        // Skip when the user is typing in a text field.
        const tag = (document.activeElement as HTMLElement | null)?.tagName ?? '';
        if (tag === 'INPUT' || tag === 'TEXTAREA') return;

        // The keys now drive the Local view-only override (setDebugTod), NOT the
        // debugSetSkyTime reducer — so they no longer mutate the shared Commons
        // sky (the prior griefing vector); they need no connection or world id.
        //   \  → jump to deep night (tod 0.0)   [ → step −0.05   ] → step +0.05
        // currentWorldTimeOfDay() returns the override-or-live tod, so stepping
        // continues from wherever the view currently is.
        let newTod: number | null = null;
        if (e.key === '\\') newTod = 0.0;
        else if (e.key === ']') newTod = stepTod(currentWorldTimeOfDay(), 0.05);
        else if (e.key === '[') newTod = stepTod(currentWorldTimeOfDay(), -0.05);
        if (newTod === null) return;

        setDebugTod(newTod);
      };
      window.addEventListener('keydown', this._keyHandler);
    } // end if (_previewEnabled)
  }

  /**
   * THE PHYSICAL SKY (2026-09-25, realism §8 R1/R2): the dome's radiance is the
   * LightingModule's light frame × the curve's lux→scene factor; the sun disc
   * dims with the deck; in rain the visible dome greys toward a CIE overcast
   * gradient (the gaps between mesh clouds stop reading clear blue); the
   * environment cube bakes the FULL overcast blend and no disc, re-baking on a
   * 0.1 cloudiness bucket, and `environmentIntensity` carries the curve between
   * bakes (the cube holds the scale it was baked at).
   */
  private lastCloudBucket = -1;
  private readonly _camW = new THREE.Vector3();
  private readonly _deckOut = { x: 0, y: 0, z: 0 };
  /** THE BAKED NIGHT's override (reused; SkyRenderer.setBakeOverrides copies it). */
  private readonly _bakeNight: SkyBakeNight = { zenith: { x: 0, y: 0, z: 0 }, horizon: { x: 0, y: 0, z: 0 }, ground: { x: 0, y: 0, z: 0 }, dome: { dirX: 1, dirZ: 0, halfWidth: 0.5, height: 0.1, color: { x: 0, y: 0, z: 0 } } };
  private _bakeNs: unknown = null;
  /** THE LIVE NIGHT: the landed cube's night term and its up-facing irradiance (scene units), recomputed per landed cube. */
  private _liveNightObj: unknown = null;
  private _liveNightE = 0;
  private _bakeELum = 0;
  private readonly _bakeParams: { zenith: [number, number, number]; horizon: [number, number, number]; ground: [number, number, number]; dome: [number, number, number, number]; domeColor: [number, number, number] } = { zenith: [0, 0, 0], horizon: [0, 0, 0], ground: [0, 0, 0], dome: [1, 0, 0.5, 0.1], domeColor: [0, 0, 0] };
  /**
   * Fill `_bakeNight` (scene radiance) for a cube that must carry `lux` of the night's ambient; returns the lux carried
   * (0 = none). The SHAPE and COLOUR are the drawn night sky's (sky_sampler.nightSkyLive — the sky shader's mirror); the
   * LEVEL is the lighting model's (nightBakeIrradianceCpu of the shape → lux). The ground under it bounces GROUND_ALBEDO.
   */
  private bakeNightTerm(lux: number, cloudiness: number, k: number): number {
    if (!(lux > 0) || !Number.isFinite(lux) || atlas.spaceFlight) { skyLightState.bakedNightLux = 0; return 0; }
    const ns = nightSkyLive(cloudiness);
    const p = this._bakeParams, z = ns.zenith, h = ns.horizonMedian, t = ns.horizonToward, d = ns.domeDir;
    const cached = ns === this._bakeNs;
    p.zenith[0] = z.x; p.zenith[1] = z.y; p.zenith[2] = z.z;
    p.horizon[0] = h.x; p.horizon[1] = h.y; p.horizon[2] = h.z;
    p.domeColor[0] = Math.max(0, t.x - h.x); p.domeColor[1] = Math.max(0, t.y - h.y); p.domeColor[2] = Math.max(0, t.z - h.z);
    p.dome[0] = d?.x ?? 1; p.dome[1] = d?.z ?? 0; p.dome[2] = d?.halfWidth ?? 0.5; p.dome[3] = skyglowLive().domes[0]?.height ?? 0.1;
    // The shape's irradiance is cached per drawn-sky sample (nightSkyLive caches ~4 Hz — 1,152 evaluations each).
    if (!cached) { const e = nightBakeIrradianceCpu(p as unknown as NightBakeParams); this._bakeNs = ns; this._bakeELum = lum709(e[0], e[1], e[2]); }
    const eLum = this._bakeELum;
    if (!(eLum > 0) || !Number.isFinite(eLum)) { skyLightState.bakedNightLux = 0; return 0; }
    const sc = (lux / eLum) * k; // drawn units → this lux of irradiance → scene radiance
    const n = this._bakeNight, gr = (GROUND_ALBEDO * lux * k) / Math.PI;
    n.zenith.x = z.x * sc; n.zenith.y = z.y * sc; n.zenith.z = z.z * sc;
    n.horizon.x = h.x * sc; n.horizon.y = h.y * sc; n.horizon.z = h.z * sc;
    n.ground.x = gr; n.ground.y = gr; n.ground.z = gr;
    n.dome.dirX = p.dome[0]; n.dome.dirZ = p.dome[1]; n.dome.halfWidth = p.dome[2]; n.dome.height = p.dome[3];
    n.dome.color.x = p.domeColor[0] * sc; n.dome.color.y = p.domeColor[1] * sc; n.dome.color.z = p.domeColor[2] * sc;
    skyLightState.bakedNightLux = +lux.toExponential(3);
    return lux;
  }
  private readonly _deckArt = { x: 0, y: 0, z: 0 };
  /** THE LATCH LAW state (lib/sky/env_carry): the sky's signature last frame, how long it held, the IBL's slewed scale. */
  private settledFrames = 0;
  private prevSig: SkySig | null = null;
  /** Scratch (the drive runs every frame — no per-frame allocation): two signatures swapped, the two cube reads, the policy input. */
  private readonly sigA: SkySig = { rs: 0, dif: 0, cloud: 0, sunY: 0 };
  private readonly sigB: SkySig = { rs: 0, dif: 0, cloud: 0, sunY: 0 };
  private readonly cubeRead: CubeSig = { rs: 0, tag: 0 };
  private readonly latRead: CubeSig = { rs: 0, tag: 0 };
  private readonly flightRead: { step: number; verdict: CubeVerdict } = { step: 0, verdict: 'fresh' };
  private readonly policyIn: BakePolicyIn = { settled: false, verdict: 'fresh', inFlight: null, entryPending: false, framesSinceImmediate: 0, movedSinceImmediate: true };
  /** The sky the last one-shot latched (a second one-shot of the SAME sky waits IMMEDIATE_GAP_FRAMES). */
  private readonly lastImmCube: CubeSig = { rs: 0, tag: 0 };
  private hasLastImm = false;
  private carryScale: number | null = null;
  private entryBakePending = true;
  private driveFrame = 0;
  private lastImmediateFrame = -1_000_000;
  private wasInSpace = false;
  /** A world entry / reload / space return: the next settled frame bakes the current sky at once, and the carrier starts fresh. */
  private markEntry(): void {
    this.entryBakePending = true;
    this.carryScale = null;
  }
  private drivePhysicalSky(app: App): void {
    const sky = this._engineSky;
    const m = lightFrame.model;
    if (!sky || !m) return;
    const scene = app.world.scene as unknown as { environmentIntensity: number };
    if (atlas.spaceFlight) { this.wasInSpace = true; if (scene.environmentIntensity !== 1) scene.environmentIntensity = 1; return; }
    if (this.wasInSpace) { this.wasInSpace = false; this.markEntry(); } // the cube is as old as the flight
    const k = exposureState.luxToScene;
    const rs = E0_LUX * k;
    const r = sky.renderer;
    const precipW = Math.min(1, lightFrame.precip / 0.3);
    const visMix = m.cloudiness * precipW;
    const Lz = overcastZenithRadiance(m.overcastLux) * k;
    const zen = { x: Lz * 0.93, y: Lz * 0.96, z: Lz };
    const gr = (GROUND_ALBEDO * m.globalLux * k) / Math.PI;
    const ground = { x: gr * 1.05, y: gr, z: gr * 0.92 };
    r.setRadianceScale(rs);
    r.setDiscScale(SUN_DISC_SCALE * m.tCloud);
    // THE LIT RAIN DECK (2026-10-06): rain swaps the night layer for this overcast dome (uNightClear = 1 − visMix), whose
    // zenith is the SUN's overcast light (photometry overcastLux — ~0 at night) — so a rainy night over a city drew a BLACK sky between amber-lit clouds
    // (night-look-A after-b). A deck over a lit town reflects the town back down (Berlin overcast ×25 its clear sky):
    // the dome takes the DRAWN artificial sky under a full deck (sky_sampler.nightSkyRadiance, the sky shader's mirror),
    // × the night factor. The visible dome only — the town's glow reaches surfaces through the hemisphere (LightingModule).
    // THE DECK'S NATURAL SHARE (2026-10-06, the wilds-black report): the dome takes the WHOLE drawn deck — the lifted
    // natural sky the deck leaves (OVERCAST_NATURAL_LEFT) AND the town's light — not the town's part alone behind a
    // town-glow gate: under rain or snow far from any town the dome was the unlifted physical night (~1/255, black)
    // over a ground drawn with the full night-floor lift (24–31/255), where a deck without rain draws 25 % of the sky.
    const nfLit = (uNightFactor as unknown as { value: number }).value;
    const live = skyglowLive();
    let zenVis = zen;
    if (visMix > 0 && nfLit > 0) {
      const lift = naturalSkyLift(Math.max(0, lightFrame.moon?.model.globalLux ?? 0)) * nightLiftScale;
      nightSkyRadiance(live.active ? live.glow : NO_SKYGLOW, live.active ? live.domes : [], 0, 1, 0, 1, lift, this._deckOut, this._deckArt);
      const o = this._deckOut, f = k * nfLit;
      if (Number.isFinite(o.x + o.y + o.z)) zenVis = { x: zen.x + o.x * f, y: zen.y + o.y * f, z: zen.z + o.z * f };
    }
    skyLightState.rainDeckArt = +((visMix > 0 && nfLit > 0 ? this._deckArt.y * k * nfLit : 0) / Math.max(1e-9, k)).toExponential(2);
    skyLightState.rainDeckNat = +((zenVis.y - zen.y) / Math.max(1e-9, k) - skyLightState.rainDeckArt).toExponential(2);
    r.setOvercast(visMix, zenVis, ground);
    // The bake latches the sky's SHAPE with its scale (sunY, cloud) so a cube of another regime can be told apart.
    // THE BAKED NIGHT (2026-10-06): the night ambient LightingModule moved out of the hemisphere (lightFrame.nightIblLux)
    // goes INTO the cube, shaped as the drawn night sky (its zenith, its horizon ring's median, the strongest dome's
    // excess) and scaled so its up-facing irradiance IS that lux — a shiny surface reflects the night (the cube was black)
    // and the ground's total ambient is unchanged. Its lux joins the carry key (`tag`, `sig.dif`), so a town's glow
    // changing under a walk re-bakes the cube through the ordinary verdict.
    const nightLux = this.bakeNightTerm(lightFrame.nightIblLux, m.cloudiness, k);
    r.setBakeOverrides({ radianceScale: rs, overcastMix: m.cloudiness, overcastZenith: zen, overcastGround: ground, tag: m.diffuseLux + nightLux, sunY: lightFrame.sinEl, cloud: m.cloudiness, night: nightLux > 0 ? this._bakeNight : undefined });
    const lb = r.lastBakedOverrides;
    // The cube holds the sky of its BAKE; between bakes the intensity carries the exposure's change AND the sky's own
    // (lighting, 2026-09-26): the diffuse lux now over the diffuse lux baked (+ the night floor, so a dark bake never
    // divides by ~0). The old exposure-only ratio held a sunset cube ×64 at midnight — the probe's midnight ground read
    // p50 172 under a black sky (webgpu-med-midnight, 03:54) until the next bake; in play every dusk re-bake gap over-lit.
    // THE LATCH LAW (lighting 3b, 2026-09-27 — lib/sky/env_carry's header has the measurements): the carrier is the
    // IBL's slewed effective scale over the cube's (a new cube re-normalizes it in the same frame; a regime jump snaps
    // with the key light), BOUNDED to ENV_MIN–ENV_MAX — a scalar cannot turn a night or storm cube into noon — and a
    // cube of another regime is re-baked at once, in one frame, from a settled frame.
    const sig = this.prevSig === this.sigA ? this.sigB : this.sigA;
    sig.rs = rs; sig.dif = m.diffuseLux + nightLux; sig.cloud = m.cloudiness; sig.sunY = lightFrame.sinEl;
    this.settledFrames = this.prevSig && !skyChanged(this.prevSig, sig) ? this.settledFrames + 1 : 0;
    this.prevSig = sig;
    this.driveFrame++;
    const cube = readCube(lb, this.cubeRead);
    const cubeS = cubeScaleOf(cube);
    const target = envScale(rs, m.diffuseLux);
    const verdict = cubeVerdict(sig, cube);
    // The carrier itself is written by `carryEnv` at RenderSub/7 — AFTER the engine's bake step at 6, against the cube
    // that is live for THIS frame's render (a one-shot or a staged cycle completing at 6 swapped it; computed here the
    // new cube rendered one frame under the old carrier — measured: env 4.000 on the frame a one-shot landed).
    const bake = sky.envBakeState;
    let inFlight: BakePolicyIn['inFlight'] = null;
    if (bake.baking) {
      this.flightRead.step = bake.step;
      this.flightRead.verdict = cubeVerdict(sig, readCube(r.latchedBakeOverrides, this.latRead));
      inFlight = this.flightRead;
    }
    const pin = this.policyIn;
    pin.settled = this.settledFrames >= SETTLE_FRAMES; pin.verdict = verdict; pin.inFlight = inFlight;
    pin.entryPending = this.entryBakePending; pin.framesSinceImmediate = this.driveFrame - this.lastImmediateFrame;
    pin.movedSinceImmediate = !this.hasLastImm || cubeVerdict(sig, this.lastImmCube) !== 'fresh';
    const act = bakeAction(pin);
    if (act === 'immediate') {
      sky.requestImmediateEnvBake(); this.entryBakePending = false; this.lastImmediateFrame = this.driveFrame; skyLightState.immediates++;
      const li = this.lastImmCube; li.rs = sig.rs; li.tag = sig.dif; li.sunY = sig.sunY; li.cloud = sig.cloud; this.hasLastImm = true;
    }
    else if (act === 'restart') { sky.restartEnvBake(); skyLightState.restarts++; }
    else if (act === 'request') { sky.requestEnvBake(); skyLightState.requests++; }
    if (act !== 'none') skyLightState.lastAction = act;
    skyLightState.settledFrames = this.settledFrames; skyLightState.verdict = verdict; skyLightState.target = target;
    skyLightState.cubeScale = cubeS ?? 0; skyLightState.entryPending = this.entryBakePending; skyLightState.bake = bake;
    const bucket = Math.round(m.cloudiness * 10);
    if (bucket !== this.lastCloudBucket) {
      if (this.lastCloudBucket >= 0) { sky.requestEnvBake(); skyLightState.rebakes++; }
      this.lastCloudBucket = bucket;
    }
    (uNightExposure as unknown as { value: number }).value = emissiveEvScale(0);
    if (skyLightState.sheets) this.driveSheets(app, k, visMix, nfLit, live);
    // The high étage: the sun at 8.5 km (little air above it), the sky's zenith as ambient, the horizon it sinks into.
    if (skyLightState.cirrus) {
      const cam = (() => { try { return app.renderer.cameras.main; } catch { return null; } })();
      if (cam) {
        cam.getWorldPosition(this._camW);
        const sp = app.world.scene.position;
        (uCirrus.camAbs.value as THREE.Vector3).set(this._camW.x - sp.x, this._camW.y - sp.y, this._camW.z - sp.z);
      }
      (uCirrus.time as unknown as { value: number }).value = (Date.now() / 1000) % 86400;
      const T = cirrusSampler.transmittance(lightFrame.sinEl, 8.5);
      const fadeC = horizonFade(lightFrame.sinEl + 0.05); // the sheet sees the sun ~3° after the ground
      const sl = rs * fadeC;
      (uCirrus.sunLight.value as THREE.Vector3).set(T.x * sl, T.y * sl, T.z * sl);
      const z = lightFrame.zenith, hz = lightFrame.horizon;
      const fl = (lightFrame.nightLux * k) / Math.PI; // sky/post (2026-09-27): the floor + the moon's global
      (uCirrus.ambient.value as THREE.Vector3).set(z.x * rs + fl * 0.5, z.y * rs + fl * 0.6, z.z * rs + fl);
      (uCirrus.horizon.value as THREE.Vector3).set(hz.x * rs, hz.y * rs, hz.z * rs);
      const cov = cirrusCoverageFor(cloudGenus.regime, lightFrame.cover) * (1 - visMix);
      (uCirrus.coverage as unknown as { value: number }).value = cov;
      skyLightState.cirrusCoverage = cov;
      const wm = app.getModule('Weather') as { windDirection?: () => { x: number; z: number } } | undefined;
      const wd = wm?.windDirection?.();
      if (wd) (uCirrus.wind.value as THREE.Vector2).set(wd.x, wd.z);
    }
    (uNightClear as unknown as { value: number }).value = 1 - visMix;
    this.driveMoonSky(k);
    this.driveNightSky(k);
    skyLightState.radianceScale = rs; skyLightState.discScale = SUN_DISC_SCALE * m.tCloud; skyLightState.overcastMix = visMix;
    skyLightState.bakeOvercast = m.cloudiness; skyLightState.lastBakedScale = lb?.radianceScale ?? 0; skyLightState.cloudBucket = bucket;
  }

  private _sheetLast = 0;
  private readonly _sheetDeck = { x: 0, y: 0, z: 0 };
  private readonly _sheetCam = { x: 0, y: 0, z: 0 };
  /**
   * THE LAYER CLOUDS, one frame (lib/sky/cloud_sheets.driveSheets — the bed calls the same): the weather's genera (or
   * the lever's pin), the light frame's own numbers, the DRAWN night deck (the sky shader's term at cloudiness 1, the
   * same `nightSkyRadiance` THE LIT RAIN DECK takes — a sheet shows it by its alpha and never adds to it), the storm's
   * flash on the low deck.
   */
  private driveSheets(app: App, k: number, visMix: number, nf: number, live: ReturnType<typeof skyglowLive>): void {
    const m = lightFrame.model;
    if (!m) return;
    const now = performance.now();
    const dt = this._sheetLast ? Math.min(0.5, (now - this._sheetLast) / 1000) : 0;
    this._sheetLast = now;
    const cam = (() => { try { return app.renderer.cameras.main; } catch { return null; } })();
    if (cam) {
      cam.getWorldPosition(this._camW);
      const sp = app.world.scene.position;
      this._sheetCam.x = this._camW.x - sp.x; this._sheetCam.y = this._camW.y - sp.y; this._sheetCam.z = this._camW.z - sp.z;
    }
    const wm = app.getModule('Weather') as { windDirection?: () => { x: number; z: number }; getPrecip?: () => { kind: string; density: number } } | undefined;
    const kind = wm?.getPrecip?.().kind;
    const seed = sheetDaySeed(); // the sky's day on the shared wall clock: every client the same sheets
    const spec = sheetPin ?? sheetsForWeather({ cover: lightFrame.cover, precip: lightFrame.precip, kind: kind === 'rain' || kind === 'snow' || kind === 'dust' ? kind : lightFrame.precip > 0 ? 'rain' : 'none', seed });
    sheetSpecNow = spec;
    // the drawn deck at the zenith under a full cloudiness (0 by day): what a sheet's thick parts show at night
    const deck = this._sheetDeck;
    deck.x = 0; deck.y = 0; deck.z = 0;
    if (nf > 0) {
      const lift = naturalSkyLift(Math.max(0, lightFrame.moon?.model.globalLux ?? 0)) * nightLiftScale;
      nightSkyRadiance(live.active ? live.glow : NO_SKYGLOW, live.active ? live.domes : [], 0, 1, 0, 1, lift, deck);
      const f = k * nf;
      if (Number.isFinite(deck.x + deck.y + deck.z)) { deck.x *= f; deck.y *= f; deck.z *= f; } else { deck.x = 0; deck.y = 0; deck.z = 0; }
    }
    const mo = cloudKey.moonEnabled ? lightFrame.moon : null;
    const r = driveSheets(sheetRt, uSheets, {
      dt, spec, seed, snap: sheetPinSnap, cam: this._sheetCam, wind: wm?.windDirection?.() ?? null, sampler: cirrusSampler,
      light: {
        k, sinEl: lightFrame.sinEl, sunDir: lightFrame.sunDir, clearGlobalLux: m.clearGlobalLux, overcastLux: m.overcastLux, cloudiness: m.cloudiness,
        sunDim: lightFrame.sunDim, skyHue: lightFrame.skyMean, nightDeck: deck, visMix,
        moon: mo ? { dir: mo.dir, sinEl: mo.sinEl, keyLux: mo.model.keyLux, keyColor: mo.model.keyColor, diffuseLux: mo.model.diffuseLux, diffuseColor: mo.model.diffuseColor, tCloud: m.tCloud } : null,
      },
    });
    sheetPinSnap = false;
    // the flash: the strike's envelope × the lightning channel ON THE EXPOSURE CURVE (the bolt's own scale — an emitter,
    // so a modest patch at noon and a white one at midnight; cloud_sheets.sheetFlashRadiance). NOT YET SEEN IN THE GAME:
    // the bed pictures this law by day and night; no world leg has run it from a real strike (impl-sheets.md).
    let flash = 0;
    const st = sheetStrike;
    if (st.live) {
      st.t += dt;
      if (st.t > FLASH_S) st.live = false;
      else flash = sheetFlashRadiance(emitterScene(EMITTER_NITS.lightning, EMITTER_DISPLAY_MAX.lightning, exposureState), strokeEnvelope(st.t, st.times), st.km);
    }
    setSheetFlash(uSheets, st.relX, st.relZ, 1500, flash);
    sheetState.flash = +flash.toPrecision(3);
    const d = r.drive, n3 = (v: number): number => +v.toPrecision(3);
    sheetState.drive = {
      low: { genus: d.low.genus, cover: n3(d.low.cover), presence: n3(d.low.presence), tau: n3(d.low.d.tauEff), meanB: n3(d.low.d.meanB), baseM: Math.round(d.low.p.baseM) },
      mid: { genus: d.mid.genus, cover: n3(d.mid.cover), presence: n3(d.mid.presence), tau: n3(d.mid.d.tauEff), meanB: n3(d.mid.d.meanB) },
      high: { genus: d.high.genus, cover: n3(d.high.cover), presence: n3(d.high.presence), tauMean: n3(d.high.tauMean) },
      moonKey: n3(r.lights.low.moonWeight), nightDeck: n3(deck.y), camY: Math.round(this._sheetCam.y),
    };
  }

  /**
   * THE NIGHT SKY's uniforms (the night-sky realism wave, 2026-10-06): sky units into the scene (UNIT_NITS ×
   * luxToScene — the physical-emissive contract), THE NIGHT-FLOOR LIFT under this moon, the stars' cap (the emissive cap
   * over the key, in units), and the artificial sky over the camera from `skyglowLive()` — the zenith glow and the four
   * strongest domes (a pinned override for beds / probes: `__nightSkyLive`).
   */
  private driveNightSky(k: number): void {
    const unitScene = UNIT_NITS * k;
    const moonG = Math.max(0, lightFrame.moon?.model.globalLux ?? 0);
    const lift = naturalSkyLift(moonG) * nightLiftScale;
    const capUnits = (keyLuminanceNits() * Math.pow(2, EMISSIVE_MAX_OVER_KEY_EV)) / UNIT_NITS;
    (uStarCap as unknown as { value: number }).value = capUnits;
    const live = skyglowLive();
    const src = nightSkyOverride ?? (live.active ? { glow: live.glow, domes: live.domes } : null);
    const ratio = src ? src.glow.ratio : 0;
    const domes = src ? src.domes.slice(0, NIGHT_DOMES) : [];
    writeNightSkyUniforms(uNightSky, { unitScene, lift, glow: { ratio, tint: src ? src.glow.tint : GLOW_TINT }, domes });
    const deck = Math.max(0, Math.min(1, lightFrame.model?.cloudiness ?? 0));
    (uNightSky.deck as unknown as { value: number }).value = Number.isFinite(deck) ? deck : 0;
    const st = nightSkyModelState;
    st.lift = +lift.toFixed(3); st.liftScale = nightLiftScale; st.unitScene = unitScene; st.capUnits = Math.round(capUnits);
    st.glowRatio = +ratio.toFixed(3); st.sqmZenith = +sqmFromUnits(1 + ratio).toFixed(2); st.nelmZenith = +nelmFromUnits(1 + ratio).toFixed(2);
    st.domes = Math.min(NIGHT_DOMES, domes.length); st.source = nightSkyOverride ? 'override' : live.active ? 'live' : 'none'; st.moonGlobalLux = +moonG.toFixed(4); st.deck = +deck.toFixed(3);
  }

  /**
   * THE MOON in the sky (2026-09-27, sky/post P2): the disc's radiance by `emissiveNits` (the contract; capped
   * MOON_DISC_MAX_OVER_KEY_EV over the key) × the air toward it (a rising moon is orange) × the phase's surface
   * brightness (the opposition surge over the lit share), and the moonlit sky = the day sky at the moon's elevation
   * × the moon's top-of-air E0 (lux → scene), tinted by the moon's spectrum.
   */
  private driveMoonSky(k: number): void {
    const mf = lightFrame.moon;
    const setV = (un: { value: unknown }, x: number, y: number, z: number): void => { (un.value as THREE.Vector3).set(x, y, z); };
    if (!mf) { setV(uMoon.radiance, 0, 0, 0); setV(uMoon.skyZenith, 0, 0, 0); setV(uMoon.skyHorizon, 0, 0, 0); setV(uMoon.aureole, 0, 0, 0); return; }
    setV(uMoon.dir, mf.dir.x, mf.dir.y, mf.dir.z);
    const T = cirrusSampler.transmittance(mf.sinEl), Tz = cirrusSampler.transmittance(1);
    const tz = Math.max(1e-6, lum709(Tz.x, Tz.y, Tz.z));
    const cr = MOON_CHROMA[0] * T.x, cg = MOON_CHROMA[1] * T.y, cb = MOON_CHROMA[2] * T.z;
    const cl = Math.max(1e-9, lum709(cr, cg, cb));
    const surface = Math.min(1, mf.model.phaseLaw / Math.max(0.05, mf.illum)); // the opposition surge over the lit share
    const nits = MOON_DISC_NITS * surface * (cl / (tz * lum709(MOON_CHROMA[0], MOON_CHROMA[1], MOON_CHROMA[2])));
    const disc = emissiveNits(nits, MOON_DISC_MAX_OVER_KEY_EV);
    setV(uMoon.radiance, (cr / cl) * disc, (cg / cl) * disc, (cb / cl) * disc);
    // The moonlit sky (per unit E0 → × the moon's E0 × luxToScene), in the moon's spectrum.
    const s = cirrusSampler.sample(mf.sinEl);
    const e = mf.model.e0Lux * k / lum709(MOON_CHROMA[0], MOON_CHROMA[1], MOON_CHROMA[2]);
    setV(uMoon.skyZenith, s.zenith.x * MOON_CHROMA[0] * e, s.zenith.y * MOON_CHROMA[1] * e, s.zenith.z * MOON_CHROMA[2] * e);
    setV(uMoon.skyHorizon, s.horizon.x * MOON_CHROMA[0] * e, s.horizon.y * MOON_CHROMA[1] * e, s.horizon.z * MOON_CHROMA[2] * e);
    // The aureole: the air's single Mie scatter of the moon at the disc (peak radiance, the lobe normalised to 1 there):
    // E0_moon × τ_Mie(slant) × p_HG(0) × T. (The first cut extrapolated the 1° ring's excess — a ring 47° from a high
    // moon — back to the disc through the HG shape: 12× too strong, a 40°-wide orange glow in the midnight shot.)
    const g = ATLAS_ATMOSPHERE.miePhaseG;
    const tauMie = (ATLAS_ATMOSPHERE.mieScattering.y * ATLAS_ATMOSPHERE.mieScaleHeight) / Math.max(0.05, mf.sinEl);
    const p0 = (1 - g * g) / (4 * Math.PI * Math.pow(1 - g, 3));
    const aur = mf.model.e0Lux * tauMie * p0 * lum709(T.x, T.y, T.z) * k * Math.max(0, Math.min(1, (mf.sinEl + 0.02) / 0.04));
    const ax = (cr / cl) * aur, ay = (cg / cl) * aur, az = (cb / cl) * aur;
    setV(uMoon.aureole, ax, ay, az);
    moonSkyState.discNits = +nits.toFixed(1); moonSkyState.discScene = +disc.toFixed(5);
    moonSkyState.skyZenith = +(lum709(s.zenith.x, s.zenith.y, s.zenith.z) * e).toExponential(3) as unknown as number;
    moonSkyState.aureole = +lum709(ax, ay, az).toExponential(3) as unknown as number;
    moonSkyState.elevationDeg = +((Math.asin(Math.max(-1, Math.min(1, mf.sinEl))) * 180) / Math.PI).toFixed(2);
  }

  /**
   * THE CARRIER (the latch law, lighting 3b 2026-09-27): `environmentIntensity` for THIS frame's render, against the
   * cube the engine left live at RenderSub/6 — the slewed effective scale over the cube's, bounded; a regime cube
   * snaps the scale with the key light. The water (RenderSub/36) and the rivers (41) copy it after this.
   */
  private carryEnv(app: App, dt: number): void {
    const sky = this._engineSky;
    const sig = this.prevSig;
    if (!sky || !lightFrame.model || !sig || atlas.spaceFlight) { lightFrame.nightIblLive = 0; return; } // in flight the drive holds env at 1
    const lb = sky.renderer.lastBakedOverrides;
    const cube = readCube(lb, this.cubeRead);
    const cubeS = cubeScaleOf(cube);
    const target = envScale(sig.rs, sig.dif);
    this.carryScale = cubeVerdict(sig, cube) === 'regime' || this.carryScale === null ? target : slewScale(this.carryScale, target, dt);
    const env = envIntensityOf(this.carryScale, cubeS);
    (app.world.scene as unknown as { environmentIntensity: number }).environmentIntensity = env;
    skyLightState.envIntensity = env; skyLightState.carryScale = this.carryScale;
    // THE LIVE NIGHT (2026-10-06): the night lux the cube that is LIVE now delivers (its baked term — once per landed
    // cube — × the carrier), which is all LightingModule may take out of the hemisphere (lib/sky/night_ibl).
    const ln = lb?.night ?? null;
    if (ln !== this._liveNightObj) { this._liveNightObj = ln; this._liveNightE = bakedNightIrradianceScene(ln); }
    lightFrame.nightIblLive = liveNightLux(this._liveNightE, env, exposureState.luxToScene);
    skyLightState.nightIblLive = +lightFrame.nightIblLive.toExponential(3);
  }

  dispose(): void {
    // Remove the time-of-day preview key listener (Task 7).
    if (this._keyHandler) {
      window.removeEventListener('keydown', this._keyHandler);
      this._keyHandler = null;
    }
    this._engineSky?.dispose();
    this._engineSky = null;
  }
}
